/**
 * Error Boundary component.
 *
 * Catches React component errors and displays a fallback UI instead of
 * crashing the entire application. React still requires a class component for
 * error boundaries (`getDerivedStateFromError` / `componentDidCatch`); the
 * fallback UI lives in the functional `ErrorFallback` component below.
 *
 * @module ErrorBoundary
 */

import React, { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircle, Home, RefreshCw } from "lucide-react";
import { logger } from "@/lib/logger";

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorFallbackProps {
  readonly error: Error | null;
  readonly errorInfo: React.ErrorInfo | null;
  readonly onReset: () => void;
  readonly onGoHome: () => void;
}

function ErrorFallback({
  error,
  errorInfo,
  onReset,
  onGoHome,
}: ErrorFallbackProps) {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <CardTitle>Something went wrong</CardTitle>
          </div>
          <CardDescription>
            An unexpected error occurred. Please try refreshing the page or
            return to the home page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {import.meta.env.DEV && error && (
            <div className="mt-4 p-4 bg-muted rounded-md">
              <p className="text-sm font-mono text-destructive mb-2">
                {error.toString()}
              </p>
              {errorInfo && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer mb-2">Stack trace</summary>
                  <pre className="overflow-auto max-h-48 whitespace-pre-wrap">
                    {errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button onClick={onReset} variant="default">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
          <Button onClick={onGoHome} variant="outline">
            <Home className="h-4 w-4 mr-2" />
            Go Home
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

/**
 * React error boundary. Must remain a class component — hooks cannot catch
 * render errors in child trees.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error("ErrorBoundary caught an error", error, {
      componentStack: errorInfo.componentStack,
    });

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    globalThis.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}
